import { useSignIn } from '@clerk/react';
import { Alert, AlertDescription, Card, CardContent, Spinner } from '@design-system';
import { CheckCircle2, Mail } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';

type Display =
  | { kind: 'pending' }
  | { kind: 'success'; title: string; body: string }
  | { kind: 'error'; title: string; body: string };

function describe(status: string | null | undefined): Display {
  switch (status) {
    case 'verified':
    case 'verified_from_the_same_client':
      return {
        kind: 'success',
        title: 'Signed in on other tab',
        body: 'Return to original tab to continue',
      };
    case 'expired':
      return {
        kind: 'error',
        title: 'This verification link has expired',
        body: 'Return to the original tab to continue.',
      };
    case 'failed':
      return {
        kind: 'error',
        title: 'This verification link is invalid',
        body: 'Return to the original tab to continue.',
      };
    case 'client_mismatch':
      return {
        kind: 'error',
        title: 'Verification link is invalid for this device',
        body: 'To continue, open the verification link on the device and browser from which you initiated the sign-in',
      };
    default:
      return { kind: 'pending' };
  }
}

export function EmailLinkSigninVerification() {
  const { signIn } = useSignIn();
  const [searchParams] = useSearchParams();
  // Clerk redirects to this page with ?__clerk_status=<state> on terminal
  // outcomes (expired/failed/client_mismatch). The SDK's in-memory sign-in
  // state isn't populated in those cases, so the URL param is the source of
  // truth — fall back to the SDK only when the URL doesn't carry a status.
  const status =
    signIn.emailLink.verification?.status ?? searchParams.get('__clerk_status') ?? null;
  const display = describe(status);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="pt-18 flex flex-1 flex-col items-center justify-center px-4 pb-12 md:pt-20">
        <div className="w-full max-w-md">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            <div className="p-1">
              <div className="bg-muted rounded-xl px-4 py-8 text-center">
                <div className="mb-2 flex items-center justify-center gap-2">
                  {display.kind === 'success' ? (
                    <CheckCircle2 className="text-primary-dark size-5" />
                  ) : (
                    <Mail className="text-primary-dark size-5" />
                  )}
                  <span className="text-primary-dark text-xs font-semibold uppercase tracking-widest">
                    Email Link
                  </span>
                </div>
                <h1 className="text-xl font-bold text-gray-950">
                  {display.kind === 'pending' ? 'Verifying your email link…' : display.title}
                </h1>
                {display.kind !== 'pending' && (
                  <p className="text-foreground mx-auto mt-1 max-w-xs text-sm">{display.body}</p>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              {display.kind === 'pending' && (
                <div className="flex items-center justify-center py-6">
                  <Spinner className="text-primary size-6" />
                </div>
              )}

              {display.kind === 'success' && (
                <Alert className="border-primary/30 bg-primary/5">
                  <AlertDescription className="text-primary-dark justify-items-center">
                    You may close this tab.
                  </AlertDescription>
                </Alert>
              )}

              {display.kind === 'error' && (
                <div className="space-y-4">
                  <Link
                    to="/"
                    className="text-primary hover:text-primary-hover block w-full text-center text-sm font-medium"
                  >
                    Back to sign in
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
